import { observer } from 'mobx-react';
import React, { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import itemsStore from '../../store/itemsStore';
import ItemApplyAvailabilityToAll from '../ItemApplyAvailabilityToAll/ItemApplyAvailabilityToAll';
import { defaultIntegrationTooltipOptions } from 'src/constants/integrations';
import { appStore } from 'src/mobx/appStore';
import integrationStore from 'src/mobx/integrationStore';
import { localeStore } from 'src/mobx/localesStore';
import { PRODUCT_ORDER_TYPE } from 'src/types/product';
import { booleanNumberReverse, getLastArrayItem, getTranslate, isDisabledByPos, isOrderTypeSupported, stringTransform } from 'src/utils/helpers';
import { numberValidation } from 'src/utils/validation';
import { Checkbox } from 'src/view/components/Controls/Checkbox/Checkbox';
import CustomTooltip, { TOOLTIP_DURATION, TOOLTIP_POSITION } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import InputAlt from 'src/view/components/Controls/InputAlt/InputAlt';
import MultiLanguageInput from 'src/view/components/Controls/MultiLanguageInput/MultiLanguageInput';
import MultiLanguageTextArea from 'src/view/components/Controls/MultiLanguageTextArea/MultiLanguageTextArea';
import LazyCategoriesPicker from 'src/view/components/Controls/Pickers/LazyCategoriesPicker/LazyCategoriesPicker';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import IntegrationTooltip from 'src/view/components/IntegrationTooltip/IntegrationTooltip';
import Text, { TEXT } from 'src/view/components/Text/Text';
import { orderTypeCheckboxOptions } from 'src/view/screens/MenuManagementScreen/screens/ItemsScreen/constants/items';
import { IOrderTypeOption } from 'src/view/screens/MenuManagementScreen/screens/ItemsScreen/types/items';
import { ProductWarning } from '~/view/components/ProductWarning';
import Icon from '~/view/components/Controls/Icon/Icon';
import { Icons } from '~/assets/fonts/Icons/icons';
import theme from '~/constants/styles';
import './style.scss';
import Picker from '~/view/components/Controls/Pickers/Picker/Picker';
import Button from '~/view/components/Controls/ButtonComponent/Button';
import { modalStore } from '~/mobx/modalStore';

const ItemMainDetails: React.FC = observer((): JSX.Element | null => {
  useEffect(() => {
    itemsStore.prepareAllergens();
  }, []);

  const selectedCategories = useMemo((): IPickerOption[] | undefined => {
    if (!itemsStore.item?.category) return undefined;

    return [{ label: getTranslate(itemsStore.item.category.name), value: itemsStore.item.category.id }];
  }, [itemsStore.item?.category]);

  const donatableSwitcherDisabled = useMemo((): boolean => {
    if (itemsStore.item?.is_donation) return false;
    return itemsStore.donatableItemsCount >= 2;
  }, [itemsStore.item?.is_donation, itemsStore.donatableItemsCount]);

  const changeMinOrderQuantity = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value: string = event.target.value.replace(/\D/, '');
    itemsStore.changeItemHandler({ min_quantity: !value || isNaN(Number(value)) ? undefined : Number(value) });
  }, []);

  const changeExternalId = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    itemsStore.changeItemHandler({ external_id: event.target.value });
  }, []);

  const changeSortNumber = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (!numberValidation(value)) return;
    itemsStore.changeItemHandler({ sort: stringTransform(value) });
  }, []);

  const selectCategory = useCallback(
    (options: IPickerOption[]) => {
      const option: IPickerOption | undefined = getLastArrayItem<IPickerOption>(options);
      itemsStore.changeItemHandler({
        category: option ? { id: Number(option.value), name: { [localeStore.language]: option.label }, status: option.status } : undefined,
      });
      // update validation
      if (!itemsStore.validation.category) itemsStore.setValidation();
    },
    [itemsStore.validation.category]
  );

  const selectAllergens = (options: IPickerOption[]) => {
    itemsStore.allergens = options;
  };

  const renderOrderTypeCheckbox = useCallback(
    (orderTypeOption: IOrderTypeOption): JSX.Element | null => {
      if (!itemsStore.item) return null;

      return (
        <Checkbox
          key={orderTypeOption.value}
          name={orderTypeOption.value}
          value={isOrderTypeSupported(itemsStore.item.order_type, orderTypeOption.value)}
          label={orderTypeOption.label}
          error={!itemsStore.validation.orderType}
          onClick={(type) => itemsStore.changeItemOrderType(type as PRODUCT_ORDER_TYPE)}
        />
      );
    },
    [itemsStore.item?.order_type, itemsStore.validation.orderType]
  );

  const donatableItems = useMemo((): Record<string, string> | undefined => {
    if (!itemsStore.donatableItems?.length) return {};
    return {
      name_of_donatable_item_1: getTranslate(itemsStore.donatableItems[0]),
      name_of_donatable_item_2: getTranslate(itemsStore.donatableItems[1]),
    };
  }, [itemsStore.donatableItems]);

  if (!itemsStore.item) return null;

  const {
    itemOrderType: orderTypeRestrictionAccess,
    itemName: nameRestrictionAccess,
    itemDescription: descRestrictionAccess,
    itemCategories: categoriesRestrictionAccess,
    donatableItemToggle: donatableRestrictionAccess,
    globalItemAvailability: availabilityRestrictionAccess,
    globalItemStock: stockRestrictionAccess,
  } = integrationStore.posRestrictions || {};

  const openNutrientModal = () => {
    modalStore.openNutrientsModal({ itemNutrients: itemsStore.item?.nutrient });
  };

  return (
    <div className="d-flex flex-column mt-4">
      {/* switchers */}
      <div className="d-flex flex-row">
        <IntegrationManager setting={availabilityRestrictionAccess} wrapperClassName="d-flex flex-row align-items-center mr-8">
          <IntegrationTooltip isVisible={isDisabledByPos(availabilityRestrictionAccess)}>
            <Switcher status={itemsStore.item?.status || 0} onChange={(status) => itemsStore.changeItemHandler({ status })} />
          </IntegrationTooltip>
          <Text type={TEXT.SWITCHER_LABEL} text="availability" disabled={isDisabledByPos(availabilityRestrictionAccess)} />
        </IntegrationManager>
        <IntegrationManager setting={stockRestrictionAccess} wrapperClassName="d-flex flex-row align-items-center mr-8">
          <IntegrationTooltip isVisible={isDisabledByPos(stockRestrictionAccess)}>
            <Switcher
              status={booleanNumberReverse(itemsStore.item?.out_of_stock)}
              onChange={(stock) => itemsStore.changeItemHandler({ out_of_stock: booleanNumberReverse(stock) })}
            />
          </IntegrationTooltip>
          <Text type={TEXT.SWITCHER_LABEL} text="in_stock" disabled={isDisabledByPos(stockRestrictionAccess)} />
        </IntegrationManager>
        <div className="d-flex flex-row align-items-center align-self-center mr-8">
          <CustomTooltip
            message="hint_availability_stock_controls_edititem"
            wrapperClassName="full-width full-height"
            contentClassName="d-flex"
            position={TOOLTIP_POSITION.PORTAL}
          >
            <Icon name={Icons.Info} size={16} color={theme.colors.graysection} />
          </CustomTooltip>
        </div>
        {appStore.settings?.donation.enabled ? (
          <IntegrationManager setting={donatableRestrictionAccess} wrapperClassName="d-flex flex-row align-items-center mr-8">
            <CustomTooltip
              disabled={!donatableSwitcherDisabled}
              message="two_items_donated"
              messageOption={donatableItems}
              maxWidth={400}
              position={TOOLTIP_POSITION.FIXED}
              duration={TOOLTIP_DURATION.EXTRA_LARGE}
            >
              <Switcher disabled={donatableSwitcherDisabled} status={itemsStore.item.is_donation || 0} onChange={itemsStore.switchDonatable} />
            </CustomTooltip>
            <Text
              type={TEXT.SWITCHER_LABEL}
              text="donatable"
              // disabled={isDisabledByPos(donatableRestrictionAccess)}
              disabled={donatableSwitcherDisabled}
            />
          </IntegrationManager>
        ) : null}
        {/* will be implemented in the future */}
        {/* <div className="d-flex flex-row align-items-center">
            <Switcher
              disabled={this.disabled}
              status={itemsStore.item?.only_for_combo || 0}
              onChange={(status) => itemsStore.changeItemHandler({ only_for_combo: status })}
            />
            <TEXT type={Text.SWITCHER_LABEL} text="only_for_combo" />
          </div> */}
      </div>
      <ItemApplyAvailabilityToAll />
      {/* main details */}
      <Text type={TEXT.SECTION_HEADER} text="main_details" className="mt-6" />
      {/* order type */}
      <IntegrationManager setting={orderTypeRestrictionAccess} wrapperClassName="d-flex flex-column mt-1">
        <Text
          type={TEXT.BLOCK_HEADER}
          text="order_type"
          className="items-main-details__name-title"
          disabled={isDisabledByPos(orderTypeRestrictionAccess)}
          required
        />
        <div className="d-flex flex-row gap-20 mt-2">
          {orderTypeCheckboxOptions.map((option) => (
            <IntegrationTooltip key={option.value} isVisible={isDisabledByPos(orderTypeRestrictionAccess)}>
              {renderOrderTypeCheckbox(option)}
            </IntegrationTooltip>
          ))}
        </div>
        {!itemsStore.validation.orderType ? (
          <Text type={TEXT.ERROR} text={itemsStore.validation.orderTypeErrorText} disabled={isDisabledByPos(orderTypeRestrictionAccess)} />
        ) : null}
      </IntegrationManager>

      <div className="d-flex flex-row mt-2">
        <div className="d-flex flex-column mr-4">
          {/* name */}
          <IntegrationManager setting={nameRestrictionAccess} wrapperClassName="d-flex flex-column">
            <Text
              type={TEXT.BLOCK_HEADER}
              text="item_name"
              className="items-main-details__name-title mb-2"
              disabled={isDisabledByPos(nameRestrictionAccess)}
              required
            />
            <MultiLanguageInput
              maxLength={75}
              enText={itemsStore.item.name.en}
              arText={itemsStore.item.name.ar}
              onChangeValue={itemsStore.changeItemName}
              validation={itemsStore.validation.name}
              tooltip={isDisabledByPos(nameRestrictionAccess) ? { ...defaultIntegrationTooltipOptions, wrapperClassName: 'full-height' } : undefined}
              disabled={isDisabledByPos(nameRestrictionAccess)}
            />
          </IntegrationManager>
          {/* category */}
          <IntegrationManager setting={categoriesRestrictionAccess} wrapperClassName="d-flex flex-column mt-5">
            <div className="d-flex flex-row">
              <Text type={TEXT.BLOCK_HEADER} text="category" className="mb-2" disabled={isDisabledByPos(categoriesRestrictionAccess)} required />
              <ProductWarning type="categoryInEditPage" product={itemsStore.item} iconSize={21} marginLeft={2} />
            </div>
            <IntegrationTooltip isVisible={isDisabledByPos(categoriesRestrictionAccess)}>
              <LazyCategoriesPicker
                searchable
                markUnavailable
                validation={{ isValid: itemsStore.validation.category, errorText: itemsStore.validation.categoryErrorText }}
                selectedOptions={selectedCategories}
                onSelect={selectCategory}
                className="items-main-details__input"
              />
            </IntegrationTooltip>
          </IntegrationManager>
        </div>
        {/* descriptions */}
        <IntegrationManager setting={descRestrictionAccess}>
          <MultiLanguageTextArea
            title="description"
            enText={itemsStore.item.description?.en}
            arText={itemsStore.item.description?.ar}
            onChangeValue={itemsStore.changeItemDescription}
            tooltip={isDisabledByPos(descRestrictionAccess) ? defaultIntegrationTooltipOptions : undefined}
            disabled={isDisabledByPos(descRestrictionAccess)}
            className="items-main-details__text-area"
          />
        </IntegrationManager>
      </div>
      <div className="d-flex flex-row mt-5">
        {/* min order quantity */}
        <div className="d-flex flex-column mr-4">
          <Text type={TEXT.BLOCK_HEADER} text="min_order_quantity" />
          <InputAlt
            type="text"
            className="items-main-details__input py-0 mt-2"
            // space required !!!
            value={itemsStore.item.min_quantity?.toString() || ' '}
            onInput={changeMinOrderQuantity}
          />
        </div>
        <div className="d-flex flex-column">
          {/* sort */}
          <Text type={TEXT.BLOCK_HEADER} text="sort" />
          <InputAlt className="items-main-details__sort py-0 mt-2" value={itemsStore.item.sort} onChange={changeSortNumber} />
        </div>
      </div>
      {/* external _id */}
      <div className="d-flex flex-column mt-5">
        <Text type={TEXT.BLOCK_HEADER} text="external_id" />
        <InputAlt className="items-main-details__input py-0 mt-2" value={itemsStore.item.external_id} onChange={changeExternalId} />
      </div>
      {/* allergens */}
      <div className="d-flex flex-column mt-5">
        <Text type={TEXT.BLOCK_HEADER} text="item_allergens_block_header" />
        <Picker
          className="items-main-details__input mt-2"
          inputClassName="items-main-details__input"
          listClassName="items-main-details__input"
          placeholder="select_allergens"
          selectedOptions={itemsStore.allergens}
          options={itemsStore.allergensOptions}
          onSelect={selectAllergens}
          numberVisibleOptionsInDropdown={8}
          notUsePlaceholderOnAllSelected
          hideResetIcon
          isMulti
          disableUnavailable={appStore.settings?.is_pos}
          markUnavailable={appStore.settings?.is_pos}
          unavailableType={'posint'}
        />
      </div>
      <div className="d-flex flex-column mt-5">
        <Button title={localeStore.t('ap_item_nutrients_btn')} onClick={openNutrientModal} appearance="tertiary" className="item_nutrients_btn" />
      </div>
    </div>
  );
});

export default ItemMainDetails;
